import type { DocsChrome } from "@brains/docs-chrome";
import type { DocsConfig, DocsHero } from "../config";
import {
  BaseEntityDataSource,
  parseMarkdownWithFrontmatter,
} from "@brains/plugins";
import type {
  BaseDataSourceContext,
  DataSourceSchema,
  BaseQuery,
  NavigationResult,
  PaginationInfo,
  EntityDataSourceConfig,
} from "@brains/plugins";
import type { Logger } from "@brains/utils/logger";
import { docSchema, type Doc } from "../schemas/doc";
import {
  docFrontmatterSchema,
  docWithDataSchema,
  type DocWithData,
} from "../schemas/doc";

export type { DocWithData };

interface DocDetailData {
  doc: DocWithData;
  docs: DocWithData[];
  prevDoc: DocWithData | null;
  nextDoc: DocWithData | null;
  chrome: DocsChrome | null;
}

interface DocListData {
  docs: DocWithData[];
  pagination: PaginationInfo | null;
  baseUrl: string | null;
  hero: DocsHero | null;
  chrome: DocsChrome | null;
}

function sortDocsForDisplay(docs: DocWithData[]): DocWithData[] {
  return [...docs].sort((a, b) => {
    const order = a.metadata.order - b.metadata.order;
    if (order !== 0) return order;
    return a.metadata.title.localeCompare(b.metadata.title);
  });
}

export function parseDocData(entity: Doc): DocWithData {
  const parsed = parseMarkdownWithFrontmatter(
    entity.content,
    docFrontmatterSchema,
  );

  return docWithDataSchema.parse({
    ...entity,
    frontmatter: parsed.metadata,
    body: parsed.content,
  });
}

export class DocDataSource extends BaseEntityDataSource<
  Doc,
  DocWithData,
  DocListData
> {
  readonly id = "docs:entities";
  readonly name = "Docs Entity DataSource";
  readonly description = "Fetches and transforms doc entities for rendering";

  protected readonly config: EntityDataSourceConfig<Doc> = {
    entityType: "doc",
    entitySchema: docSchema,
    defaultSort: [
      { field: "order" as const, direction: "asc" as const },
      { field: "section" as const, direction: "asc" as const },
      { field: "title" as const, direction: "asc" as const },
    ],
    defaultLimit: 100,
    enableNavigation: true,
  };

  /** The configured hero and chrome, handed to every docs page. */
  private readonly hero: DocsHero | null;
  private readonly chrome: DocsChrome | null;

  constructor(logger: Logger, presentation: DocsConfig = {}) {
    super(logger);
    this.hero = presentation.hero ?? null;
    this.chrome = presentation.chrome ?? null;
  }

  protected transformEntity(entity: Doc): DocWithData {
    return parseDocData(entity);
  }

  override async fetch<T>(
    query: unknown,
    outputSchema: DataSourceSchema<T>,
    context: BaseDataSourceContext,
  ): Promise<T> {
    const params = this.parseQuery(query);

    if (!params.query.id) {
      const list = await this.fetchList(
        { ...params.query, limit: 1000, page: undefined, pageSize: undefined },
        context.entityService,
      );
      return outputSchema.parse(
        this.buildListResult(list.items, null, params.query),
      );
    }

    const [detail, list] = await Promise.all([
      this.fetchDetail(params.query.id, context.entityService),
      this.fetchList({ limit: 1000 }, context.entityService),
    ]);

    const docs = sortDocsForDisplay(list.items);
    const currentIndex = docs.findIndex((item) => item.id === detail.item.id);
    const prevDoc = currentIndex > 0 ? (docs[currentIndex - 1] ?? null) : null;
    const nextDoc =
      currentIndex >= 0 && currentIndex < docs.length - 1
        ? (docs[currentIndex + 1] ?? null)
        : null;

    const data: DocDetailData = {
      doc: detail.item,
      docs,
      prevDoc,
      nextDoc,
      chrome: this.chrome,
    };
    return outputSchema.parse(data);
  }

  protected override buildDetailResult(
    item: DocWithData,
    navigation: NavigationResult<DocWithData> | null,
  ): DocDetailData {
    return {
      doc: item,
      docs: [item],
      prevDoc: navigation?.prev ?? null,
      nextDoc: navigation?.next ?? null,
      chrome: this.chrome,
    };
  }

  protected buildListResult(
    items: DocWithData[],
    pagination: PaginationInfo | null,
    query: BaseQuery,
  ): DocListData {
    return {
      docs: sortDocsForDisplay(items),
      pagination,
      baseUrl: query.baseUrl ?? null,
      hero: this.hero,
      chrome: this.chrome,
    };
  }
}
